import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { ExportResponse, ExportToExcelService } from '../../services/export-to-excel.service';

@Component({
  selector: 'app-export-to-excel',
  templateUrl: './export-to-excel.component.html',
  styleUrl: './export-to-excel.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportToExcelComponent {
  @Input({ required: true }) columnNames!: Record<string, string>;
  @Input({ required: true }) dataFetcher!: (selectedColumns?: string[]) => Observable<ExportResponse>;
  @Input() fileName: string = 'export';
  @Input() extraColumns?: Record<string, string>;
  @Output() exportStarted = new EventEmitter<void>();

  isExporting = false;

  private readonly exportService = inject(ExportToExcelService);
  private readonly alertService = inject(AlertService);
  private readonly cdr = inject(ChangeDetectorRef);

  onExportClick(): void {
    if (this.isExporting) return;
    if (!this.columnNames || Object.keys(this.columnNames).length === 0) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: 'Please select at least one column before exporting.'
      } as Alert);
      return;
    }
    this.isExporting = true;
    this.exportStarted.emit();
    const selectedKeys = Object.keys(this.columnNames || {});
    this.dataFetcher(selectedKeys).subscribe({
      next: (response) => {
        this.isExporting = false;
        this.cdr.markForCheck();
        if (!response?.data || response.data.length === 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'No data available to export.'
          } as Alert);
          return;
        }
        try {
          this.exportService.exportToXls(response.data, this.columnNames, this.fileName, this.extraColumns);
        } catch {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Export failed. Please try again.'
          } as Alert);
        }
      },
      error: () => {
        this.isExporting = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Export failed. Please try again.'
        } as Alert);
      }
    });
  }
}
